/* File: assets/card-slab.js
   Graded Slab — strict + company-only fallback, with Binder-style compat
   Updated to support TAG grading (condition words & numeric grades)
*/

(function () {
  /* ---------------------------------------
     Small helpers
  --------------------------------------- */
  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn, { once: true });
  }

  // Normalize/cap grade → max 10; strip trailing ".0"
  function normalizeGrade(raw) {
    const n = parseFloat(String(raw).replace(",", "."));
    if (!isFinite(n)) return "";
    const capped = Math.min(n, 10);
    const fixed = Math.round(capped * 10) / 10;
    return String(fixed).replace(/\.0$/, "");
  }

  /* ---------------------------------------
     TAG condition → numeric grade mapping
  --------------------------------------- */
  const TAG_CONDITION_MAP = {
    "pristine": 10,
    "gem mint": 10,
    "mint": 9,
    "nm mt+": 8.5,
    "nm mt": 8,
    "nm+": 7.5,
    "nm": 7,
    "ex mt+": 6.5,
    "ex mt": 6,
    "ex+": 5.5,
    "ex": 5,
    "vg ex+": 4.5,
    "vg ex": 4,
    "vg+": 3.5,
    "vg": 3,
    "good+": 2.5,
    "good": 2,
    "fair": 1.5,
    "poor": 1,
  };

  /* ---------------------------------------
     Company patterns
       - gradeRx: tags that include company + numeric grade
       - onlyRx:  tags that include company only (no grade)
  --------------------------------------- */
  const COMPANY_MAP = {
    psa:      { slabClass: "slab--psa",      gradeRx: [/^PSA\s*([0-9]+(?:\.[0-9])?)$/i],                   onlyRx: [/^PSA$/i] },
    bgs:      { slabClass: "slab--bgs",      gradeRx: [/^(?:BGS|Beckett)\s*([0-9]+(?:\.[0-9])?)$/i],       onlyRx: [/^(?:BGS|Beckett)$/i] },
    cgc:      { slabClass: "slab--cgc",      gradeRx: [/^CGC\s*([0-9]+(?:\.[0-9])?)$/i],                   onlyRx: [/^CGC$/i] },
    sgc:      { slabClass: "slab--sgc",      gradeRx: [/^SGC\s*([0-9]+(?:\.[0-9])?)$/i],                   onlyRx: [/^SGC$/i] },
    gradelat: { slabClass: "slab--gradelat", gradeRx: [/^(?:Gradelat|GradeLat)\s*([0-9]+(?:\.[0-9])?)$/i], onlyRx: [/^(?:Gradelat|Grade\s*Lat)$/i] },
    valueup:  { slabClass: "slab--valueup",  gradeRx: [/^(?:Value\s*Up|ValueUp)\s*([0-9]+(?:\.[0-9])?)$/i],onlyRx: [/^(?:Value\s*Up|ValueUp)$/i] },
    gxi:      { slabClass: "slab--gxi",      gradeRx: [/^GXI\s*([0-9]+(?:\.[0-9])?)$/i],                   onlyRx: [/^GXI$/i] },
    otro:     { slabClass: "slab--otro",     gradeRx: [/^OtroGraded\s*([0-9]+(?:\.[0-9])?)$/i], onlyRx:            [/^OtroGraded$/i] },
    tag:      { slabClass: "slab--tag",      gradeRx: [/^TAG\s*([0-9]+(?:\.[0-9])?)$/i],                   onlyRx: [/^TAG$/i] },
  };

  // Display names for composite tag & badge aria-label
  const COMPANY_DISPLAY = {
    psa: "PSA",
    bgs: "BGS",
    cgc: "CGC",
    sgc: "SGC",
    gradelat: "GradeLat",
    valueup: "ValueUp",
    gxi: "GXI",
    otro: "OTRO GRADED",
    tag: "TAG",
  };

  /* ---------------------------------------
     Language flag icons (tag → URL)
     Tags are case-insensitive: EnglishTCG, JapaneseTCG, etc.
  --------------------------------------- */
  const LANG_ICON_MAP = {
    englishtcg: {
      key: "english",
      label: "English TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/united-states_1.png?v=1764112155",
    },
    japanesetcg: {
      key: "japanese",
      label: "Japanese TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/japan_1.png?v=1764112155",
    },
    frenchtcg: {
      key: "french",
      label: "French TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/france_1.png?v=1764112155",
    },
    germantcg: {
      key: "german",
      label: "German TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/germany_1.png?v=1764112155",
    },
    spanishtcg: {
      key: "spanish",
      label: "Spanish TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/mexico_1.png?v=1764112155",
    },
    italiantcg: {
      key: "italian",
      label: "Italian TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/italy_1.png?v=1764112155",
    },
    portuguesetcg: {
      key: "portuguese",
      label: "Portuguese TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/portugal_1.png?v=1764112155",
    },
    koreantcg: {
      key: "korean",
      label: "Korean TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/south-korea_1.png?v=1764112155",
    },
    chinesetcg: {
      key: "chinese",
      label: "Chinese TCG",
      url: "https://cdn.shopify.com/s/files/1/0731/4128/9172/files/china_1.png?v=1764112264",
    },
  };

  /* ---------------------------------------
     Extra tag detectors
  --------------------------------------- */
  const RX_BGS_BLACK_LABEL = /Black\s*Label\s*Pristine/i;
  const RX_CGC_PRISTINE    = /\bPristine\b/i;
  const RX_SGC_PRISTINE    = /\b(PRI|Pristine)\b/i;
  const RX_GEM             = /\bGEM\b/i;
  const RX_GEM_MINT        = /\bGEM\s*MINT\b/i;

  function addTagUnique(list, tag) {
    const needle = String(tag).trim().toLowerCase();
    if (!needle) return;
    const exists = list.some(t => String(t).trim().toLowerCase() === needle);
    if (!exists) list.push(tag);
  }

  /* ---------------------------------------
     Parse helpers → detect company/grade
  --------------------------------------- */
  function parseCompanyGrade(tags = []) {
    const list = (tags || []).map(t => String(t).trim()).filter(Boolean);
    for (const [key, cfg] of Object.entries(COMPANY_MAP)) {
      for (const t of list) {
        // Numeric grade regex
        for (const rx of cfg.gradeRx) {
          const m = t.match(rx);
          if (!m) continue;
          const rawNum = parseFloat(String(m[1]).replace(",", "."));
          if (!isFinite(rawNum)) continue;
          const capped = Math.min(rawNum, 10);
          return {
            slabClass: cfg.slabClass,
            gradeStr: normalizeGrade(capped),
            gradeNum: capped,
            companyKey: key,
            tags: list,
          };
        }
        // TAG condition words (only for TAG company) – DESACTIVADO para evitar falsos positivos
// if (key === "tag") {
//   const lower = t.toLowerCase();
//   for (const [condition, gradeNum] of Object.entries(TAG_CONDITION_MAP)) {
//     if (lower === condition || lower === `tag ${condition}`) {
//       return {
//         slabClass: cfg.slabClass,
//         gradeStr: normalizeGrade(gradeNum),
//         gradeNum: gradeNum,
//         companyKey: key,
//         tags: list,
//       };
//     }
//   }
// }
      }
    }
    return null;
  }

  function parseCompanyOnly(tags = []) {
    const list = (tags || []).map(t => String(t).trim()).filter(Boolean);
    for (const [key, cfg] of Object.entries(COMPANY_MAP)) {
      if ((cfg.onlyRx || []).some(rx => list.some(t => rx.test(t)))) {
        return {
          slabClass: cfg.slabClass,
          gradeStr: "",
          gradeNum: 0,
          companyKey: key,
          tags: list,
          noGrade: true
        };
      }
    }
    return null;
  }

  /* ---------------------------------------
     DOM helpers (unchanged)
  --------------------------------------- */
  function getCards() {
    const a = Array.from(document.querySelectorAll(".card.product-card"));
    const b = Array.from(document.querySelectorAll(".product-card[data-tags], [data-tags].product-card, [data-tags]:not(.card)"));
    const seen = new Set();
    return [...a, ...b].filter(el => {
      if (seen.has(el)) return false;
      seen.add(el);
      return true;
    });
  }

  function getTagsStr(node) {
    return (node?.dataset?.tags || node?.getAttribute?.("data-tags") || "").trim();
  }

  function findMediaContainer(card) {
    return (
      card.querySelector(".product-image-wrapper") ||
      card.querySelector(".card__media .media") ||
      card.querySelector(".media") ||
      card.querySelector('a[href^="/products/"]')?.parentElement ||
      card.querySelector(".card-link")?.parentElement ||
      null
    );
  }

  function findImageAnchor(card) {
    let a = card.querySelector('.product-image-wrapper a[href^="/products/"]') ||
            card.querySelector('.card__media .media a[href^="/products/"]');
    if (a) return a;
    const img = card.querySelector('a[href^="/products/"] img, img');
    return img ? img.closest("a") : null;
  }

  function findTitleEl(card) {
    return card.querySelector(".card__heading, h3");
  }

  /* ---------------------------------------
     Language flag helper (unchanged)
  --------------------------------------- */
  function addLanguageIcon(card, tags) {
    if (window.NENSONS_DISABLE_LANG_ICONS) return;
    if (card.closest && card.closest("product-info")) return;
    if (card.dataset.langIconDone === "1") return;
    if (!tags || !tags.length) return;

    const media = findMediaContainer(card);
    if (!media) return;

    let match = null;
    for (const rawTag of tags) {
      const key = String(rawTag).trim().toLowerCase();
      if (!key) continue;
      const info = LANG_ICON_MAP[key];
      if (info) {
        match = info;
        break;
      }
    }

    if (!match) return;

    const icon = document.createElement("div");
    icon.className = `product-lang-icon product-lang-icon--${match.key}`;

    const img = document.createElement("img");
    img.src = match.url;
    img.alt = match.label;
    img.loading = "lazy";

    icon.appendChild(img);
    media.appendChild(icon);
    card.dataset.langIconDone = "1";
  }

  function moveLanguageIconsIntoSlab(media, slab) {
    if (!media || !slab) return;
    const icons = media.querySelectorAll(".product-lang-icon");
    icons.forEach(icon => {
      if (icon.parentNode === slab) return; // already correct
      slab.appendChild(icon);
    });
  }

  /* ---------------------------------------
     Variant classes per company
  --------------------------------------- */
  function computeVariantClasses(info) {
    const classes = [info.slabClass];

    switch (info.companyKey) {
      case "bgs": {
        const hasBlackLabel = info.gradeNum === 10 && info.tags.some(t => RX_BGS_BLACK_LABEL.test(t));
        if (hasBlackLabel) {
          classes.push("slab--bgs-blacklabel");
        } else if (info.gradeNum >= 9.5) {
          classes.push("slab--bgs-gold");
        } else {
          classes.push("slab--bgs-silver");
        }
        break;
      }
      case "cgc": {
        const isPristine10 = info.gradeNum === 10 && info.tags.some(t => RX_CGC_PRISTINE.test(t));
        classes.push(isPristine10 ? "slab--cgc-pristine10" : "slab--cgc-silver");
        break;
      }
      case "sgc": {
        const isTen = info.gradeNum === 10;
        const isPristine = isTen && info.tags.some(t => RX_SGC_PRISTINE.test(t));
        classes.push(isPristine ? "slab--sgc-pristine10" : (isTen ? "slab--sgc-gem10" : "slab--sgc-silver"));
        break;
      }
      case "valueup": {
        if (info.gradeNum === 10) classes.push("slab--valueup-10");
        break;
      }
      // TAG: no extra variant classes (optional)
    }

    if (info.noGrade) classes.push("slab--no-grade");
    return classes.join(" ");
  }

  /* ---------------------------------------
     Top-bar mode (unchanged)
  --------------------------------------- */
  function wantsTopBar(card) {
    const host = card.closest("[data-grid-size]") || card.closest("[data-grid]");
    const g = host && ((host.getAttribute("data-grid-size") || host.getAttribute("data-grid")) || "");
    if (g === "4" || g === "5") return { on: true, sm: g === "4" };
    const rect = card.getBoundingClientRect?.();
    const w = rect?.width || card.offsetWidth || 0;
    if (w && w < 260) return { on: true, sm: w < 220 };
    return { on: false, sm: false };
  }

  let __resizeRAF = 0;
  let __langTiltRAF = 0;

  function applyTopBarModeOnResize() {
    if (__resizeRAF) cancelAnimationFrame(__resizeRAF);
    __resizeRAF = requestAnimationFrame(() => {
      document.querySelectorAll(".slab").forEach(slab => {
        const card = slab.closest(".product-card, .card");
        if (!card) return;
        const { on, sm } = wantsTopBar(card);
        slab.classList.toggle("slab--badge-topbar", on);
        slab.classList.toggle("slab--badge-topbar-sm", on && sm);
      });
      __resizeRAF = 0;
    });
  }

  /* ---------------------------------------
     Language icon tilt sync (unchanged)
  --------------------------------------- */
  function startLangIconTiltSync() {
    if (__langTiltRAF) return;
    const loop = () => {
      const icons = document.querySelectorAll(".product-lang-icon");
      if (icons.length) {
        icons.forEach(icon => {
          const card = icon.closest(".product-card, .card, .card-tilt");
          if (!card) return;
          const hasSlab = !!card.querySelector(".slab");
          if (hasSlab) {
            icon.style.transform = "";
            icon.style.transformOrigin = "";
            return;
          }
          const media = findMediaContainer(card);
          if (!media) return;
          const img = media.querySelector("img");
          if (!img) return;
          const cs = window.getComputedStyle(img);
          const t = cs.transform;
          const origin = cs.transformOrigin;
          if (t && t !== "none") {
            icon.style.transform = t;
            icon.style.transformOrigin = origin;
          } else {
            icon.style.transform = "";
            icon.style.transformOrigin = "";
          }
        });
      }
      __langTiltRAF = requestAnimationFrame(loop);
    };
    __langTiltRAF = requestAnimationFrame(loop);
  }

  /* ---------------------------------------
     Main upgrader (unchanged logic, now works with TAG)
  --------------------------------------- */
  function upgrade() {
    getCards().forEach(card => {
      const raw = getTagsStr(card);
      if (!raw) return;
      const tags = raw.split("|").map(s => s.trim()).filter(Boolean);
      if (!tags.length) return;

      const media = findMediaContainer(card);
      if (!media) return;

      const preexistingSlab = media.querySelector(".slab");

      if (preexistingSlab) {
        addLanguageIcon(card, tags);
        moveLanguageIconsIntoSlab(media, preexistingSlab);
        media.classList.add("uses-slab");
        card.dataset.slabDone = "1";
        return;
      }

      addLanguageIcon(card, tags);

      if (card.dataset.slabDone === "1") return;

      let info = parseCompanyGrade(tags);
      if (!info) info = parseCompanyOnly(tags);
      if (!info) return;

      const hasGem = tags.some(t => RX_GEM.test(t) || RX_GEM_MINT.test(t));
      const outTags = [...tags];
      if (hasGem) {
        addTagUnique(outTags, "GEM");
        if (info.gradeStr) {
          const displayName = COMPANY_DISPLAY[info.companyKey] || (info.companyKey || "").toUpperCase();
          addTagUnique(outTags, `${displayName} ${info.gradeStr} GEM`);
        }
      }
      const updatedTagsStr = outTags.join(" | ");

      const isGradeLat = info.companyKey === "gradelat";
      const isTen = info.gradeStr === "10" || info.gradeStr === "10.0";
      const shouldMarkGradeLatGold = isGradeLat && isTen && hasGem;

      const anchor  = findImageAnchor(card);
      const titleEl = findTitleEl(card);
      if (!anchor || !titleEl) return;

      const slab = document.createElement("div");
      slab.className = `slab ${computeVariantClasses(info)}`;
      if (shouldMarkGradeLatGold) slab.classList.add("slab--gradelat-10-gem");
      if (info.gradeStr) slab.setAttribute("data-grade", info.gradeStr);
      slab.setAttribute("data-tags", updatedTagsStr);

      const label = document.createElement("div");
      label.className = "slab-label";

      const titleSpan = document.createElement("span");
      titleSpan.className = "slab-title";
      titleSpan.textContent = (titleEl.textContent || "").trim();
      label.appendChild(titleSpan);

      if (info.gradeStr) {
        const gEl = document.createElement("div");
        gEl.className = "slab-grade";
        gEl.textContent = info.gradeStr;
        label.appendChild(gEl);
      }

      const win = document.createElement("div");
      win.className = "slab-window";
      anchor.classList.add("slab-link");
      win.appendChild(anchor);

      const badge = document.createElement("div");
      badge.className = "slab-badge";
      const companyText = (COMPANY_DISPLAY[info.companyKey] || info.companyKey || "").toUpperCase();
      badge.setAttribute("aria-label", companyText);

      slab.appendChild(label);
      slab.appendChild(badge);
      slab.appendChild(win);
      media.appendChild(slab);
      media.classList.add("uses-slab");

      moveLanguageIconsIntoSlab(media, slab);

      titleEl.style.display = "none";
      card.dataset.tags = updatedTagsStr;

      const { on, sm } = wantsTopBar(card);
      if (on) slab.classList.add("slab--badge-topbar");
      if (on && sm) slab.classList.add("slab--badge-topbar-sm");

      try {
        card.dispatchEvent(new CustomEvent("product:tags-updated", {
          bubbles: true,
          detail: { tags: outTags }
        }));
      } catch (_) {}

      card.dataset.slabDone = "1";
    });

    applyTopBarModeOnResize();
    startLangIconTiltSync();
  }

  ready(upgrade);
  window.addEventListener("resize", applyTopBarModeOnResize, { passive: true });
  document.addEventListener("shopify:section:load", upgrade);
  document.addEventListener("shopify:section:reorder", upgrade);
})();

(function randomizeSlabShine() {
  function setRandomDelay(slab) {
    // Positive delay staggers each card's sweep independently
    // Spread across the full 8s cycle so they don't all fire at once
    const delay = (Math.random() * 8).toFixed(2);
    slab.style.setProperty('--shine-delay', `${delay}s`);
  }

  function processAllSlabs() {
    document.querySelectorAll('.product-image-wrapper.uses-slab > .slab').forEach(slab => {
      // Only set once — don't re-randomize on every mutation
      if (slab.dataset.shineSet === '1') return;
      setRandomDelay(slab);
      slab.dataset.shineSet = '1';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processAllSlabs);
  } else {
    processAllSlabs();
  }

  const observer = new MutationObserver((mutations) => {
    let needsUpdate = false;
    for (const m of mutations) {
      if (m.addedNodes.length) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && (node.matches?.('.slab') || node.querySelector?.('.slab'))) {
            needsUpdate = true;
            break;
          }
        }
      }
    }
    if (needsUpdate) processAllSlabs();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', processAllSlabs);
  document.addEventListener('binder:data-ready', processAllSlabs);
  
})();